import { useEffect, useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { api, API_URL } from './api';
import {
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  SectionCard,
  StatusBadge,
  formatDate,
} from './components';

export function ReportsView({ role = '' }: { role?: string }) {
  const canWrite = [
    'tenant_owner',
    'tenant_admin',
    'tenant_operator',
    'aot_admin',
    'aot_operator',
  ].includes(role);
  const [items, setItems] = useState<any[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () =>
    api<any>('/v1/operations/reports')
      .then((result) => {
        setItems(result.items);
        setError('');
      })
      .catch((reason) => {
        setItems([]);
        setError(reason instanceof Error ? reason.message : 'Report library unavailable.');
      });
  useEffect(() => {
    void load();
  }, []);
  async function generate() {
    if (!canWrite) return;
    setBusy(true);
    const end = new Date();
    const start = new Date(end);
    start.setUTCMonth(start.getUTCMonth() - 1);
    try {
      await api('/v1/operations/reports', {
        method: 'POST',
        body: JSON.stringify({
          type: 'health',
          periodStart: start.toISOString(),
          periodEnd: end.toISOString(),
        }),
      });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Report generation failed.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHeader
        eyebrow="MANAGED REPORTING"
        title="Reports"
        description="Tenant-private operational reports with authenticated HTML and PDF delivery."
        actions={
          canWrite && (
            <button className="button primary" onClick={generate} disabled={busy}>
              {busy ? 'Generating…' : 'Generate health report'}
            </button>
          )
        }
      />
      <SectionCard>
        {error ? (
          <ErrorState message={error} onRetry={load} />
        ) : !items ? (
          <LoadingState />
        ) : items.length ? (
          <DataTable label="Reports">
            <thead>
              <tr>
                <th>Report type</th>
                <th>Period</th>
                <th>Generated</th>
                <th>Report timezone</th>
                <th>Status</th>
                <th>Key highlights</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.reportType}</strong>
                  </td>
                  <td>
                    {formatDate(item.periodStart, false)} – {formatDate(item.periodEnd, false)}
                  </td>
                  <td>{formatDate(item.generatedAt)}</td>
                  <td>
                    {item.timeZone ?? 'UTC'}
                    {item.utcOffsetMinutes != null
                      ? ` · UTC${Number(item.utcOffsetMinutes) >= 0 ? '+' : '-'}${String(Math.floor(Math.abs(Number(item.utcOffsetMinutes)) / 60)).padStart(2, '0')}:${String(Math.abs(Number(item.utcOffsetMinutes)) % 60).padStart(2, '0')}`
                      : ''}
                  </td>
                  <td>
                    <StatusBadge value={item.status} />
                  </td>
                  <td>
                    {item.highlights?.length ? (
                      <ul className="report-highlights">
                        {item.highlights.slice(0, 3).map((highlight: string) => (
                          <li key={highlight}>{highlight}</li>
                        ))}
                      </ul>
                    ) : (
                      'Open preview for executive detail'
                    )}
                  </td>
                  <td>
                    <div className="download-actions">
                      <a
                        href={`${API_URL}/v1/operations/reports/${item.id}/download?format=html`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FileText size={14} /> Preview
                      </a>
                      <a href={`${API_URL}/v1/operations/reports/${item.id}/download?format=pdf`}>
                        <Download size={14} /> PDF
                      </a>
                      <a href={`${API_URL}/v1/operations/reports/${item.id}/download?format=html`}>
                        <Download size={14} /> HTML
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <EmptyState
            title="No reports yet"
            description={
              canWrite
                ? 'Generate a health report when enough operational evidence is available.'
                : 'A workspace administrator or operator can generate a report when operational evidence is available.'
            }
            action={
              canWrite && (
                <button className="button primary" onClick={generate}>
                  Generate first report
                </button>
              )
            }
          />
        )}
      </SectionCard>
    </>
  );
}
